'use client';

/* eslint-disable react/jsx-props-no-spreading */
import { DEFAULT_COUNTRY } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { useCombobox } from 'downshift';
import noop from 'lodash/noop';
import take from 'lodash/take';
import type React from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import usePlacesAutocomplete from 'use-places-autocomplete';

import type { InputProps } from '../internal/input';
import Input from '../internal/input';
import type { AddressPrediction } from './address-input.types';
import AddressDropdownFooter from './components/address-dropdown-footer';
import AddressDropdownItem from './components/address-dropdown-item';
import usePopper from './hooks/use-popper';

const MAX_OF_RESULTS = 5;

export type AddressInputProps = Omit<InputProps, 'onSelect'> & {
  onSelect?: (prediction?: AddressPrediction | null) => void;
  country?: Iso3166TwoDigitCountryCode;
};

const AddressInput = forwardRef<HTMLInputElement, AddressInputProps>(
  (
    {
      country = DEFAULT_COUNTRY.value,
      onBlur = noop,
      onChange = noop,
      onChangeText,
      onKeyDown = noop,
      onSelect = noop,
      value,
      ...props
    }: AddressInputProps,
    ref,
  ) => {
    const isUncontrolled = value === undefined;
    const localRef = useRef<HTMLInputElement>(null);
    const { setReferenceElement, setPopperElement, popper } = usePopper();
    const {
      suggestions: { data },
      setValue,
      clearCache,
      clearSuggestions,
    } = usePlacesAutocomplete({
      requestOptions: {
        componentRestrictions: {
          country,
        },
      },
    });

    useEffect(() => {
      clearCache();
      clearSuggestions();
    }, [country, clearCache, clearSuggestions]);

    const options = take(data, MAX_OF_RESULTS);
    const {
      getComboboxProps,
      getInputProps,
      getItemProps,
      getMenuProps,
      getToggleButtonProps,
      highlightedIndex,
      isOpen: downShiftIsOpen,
    } = useCombobox({
      items: options,
      itemToString: item => (item ? item.structured_formatting.main_text : ''),
      onSelectedItemChange: ({ selectedItem }) => {
        onChangeText?.(selectedItem?.structured_formatting.main_text || '');
        onSelect(selectedItem);

        const hasToTriggerChangeEvent = isUncontrolled && localRef.current;
        if (hasToTriggerChangeEvent && localRef.current) {
          const event = new window.Event('change', { bubbles: true });
          localRef.current.dispatchEvent(event);
        }
      },
      onInputValueChange: ({ inputValue: nextInputValue = '' }) => {
        onChangeText?.(nextInputValue);
        setValue(nextInputValue);
      },
    });
    const comboBoxProps = getComboboxProps();
    const toggleButtonProps = getToggleButtonProps();
    const inputProps = getInputProps();
    const menuProps = getMenuProps({}, { suppressRefError: true });
    const hasResults = options.length > 0;
    const isDropdownOpen = downShiftIsOpen && hasResults;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      inputProps.onChange(event);
      onChange(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      inputProps.onBlur(event);
      onBlur(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      inputProps.onKeyDown(event);
      onKeyDown(event);
    };

    const renderAddressItem = (item: AddressPrediction, index: number) => {
      const itemProps = getItemProps({ item, index });
      return (
        <AddressDropdownItem
          data-dd-privacy="mask"
          disableHoverStyles={highlightedIndex !== -1}
          highlighted={highlightedIndex === index}
          key={item.place_id}
          subtitle={item.structured_formatting.secondary_text}
          title={item.structured_formatting.main_text}
          {...itemProps}
        />
      );
    };

    return (
      <div {...comboBoxProps}>
        <div ref={setReferenceElement} className="relative">
          <Input
            {...props}
            {...toggleButtonProps}
            aria-autocomplete={inputProps['aria-autocomplete']}
            aria-controls={inputProps['aria-controls']}
            autoComplete="address-line1"
            id={props.id || inputProps.id}
            onBlur={handleBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            value={value}
            data-dd-privacy="mask"
            ref={mergeRefs([localRef, inputProps.ref, toggleButtonProps.ref, ref])}
          />
          {isDropdownOpen ? (
            <ul
              className="fp-dropdown-menu max-h-[332px] w-full outline-none pt-2"
              {...menuProps}
              {...popper.attributes.popper}
              ref={mergeRefs([menuProps.ref, setPopperElement])}
              style={popper.styles.popper}
              data-dd-privacy="mask"
            >
              {options.map(renderAddressItem)}
              <AddressDropdownFooter />
            </ul>
          ) : null}
        </div>
      </div>
    );
  },
);

export default AddressInput;
